import {useEffect, useRef} from 'react';

import type {AriaButtonProps} from '@react-types/button';
import type {
  DOMAttributes,
  FocusableElement,
  InputBase,
  RangeInputBase,
  Validation,
  ValueBase,
} from '@react-types/shared';
import type {KeyboardEventHandler} from 'react';
import {useEffectEvent, useGlobalListeners} from '../utils';
import {announce, clearAnnouncer} from '../live-announcer';
import {useLocalizedStringFormatter} from '../i18n';
import messages from './intl';

type Timeout = ReturnType<typeof setTimeout>;

export interface SpinButtonProps
  extends InputBase,
    Validation<number>,
    ValueBase<number>,
    RangeInputBase<number> {
  textValue?: string;
  onIncrement?: () => void;
  onIncrementPage?: () => void;
  onDecrement?: () => void;
  onDecrementPage?: () => void;
  onDecrementToMin?: () => void;
  onIncrementToMax?: () => void;
}

export interface SpinbuttonAria {
  spinButtonProps: DOMAttributes;
  incrementButtonProps: AriaButtonProps;
  decrementButtonProps: AriaButtonProps;
}

const isValidNumber = (value: number | undefined): value is number =>
  typeof value === 'number' && !isNaN(value);

export function useSpinButton(props: SpinButtonProps): SpinbuttonAria {
  const _async = useRef<Timeout>();
  const {
    value,
    textValue,
    minValue,
    maxValue,
    isDisabled,
    isReadOnly,
    isRequired,
    onIncrement,
    onIncrementPage,
    onDecrement,
    onDecrementPage,
    onDecrementToMin,
    onIncrementToMax,
  } = props;
  const stringFormatter = useLocalizedStringFormatter(messages);
  const clearAsync = () => clearTimeout(_async.current);

  useEffect(() => {
    return () => clearAsync();
  }, []);

  const onKeyDown: KeyboardEventHandler<FocusableElement> = (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || isReadOnly) {
      return;
    }

    switch (e.key) {
      // @ts-expect-error - Fallthrough is intentional
      case 'PageUp':
        if (onIncrementPage) {
          e.preventDefault();
          onIncrementPage();
          break;
        }
      // fallthrough!
      case 'ArrowUp':
      case 'Up':
        if (onIncrement) {
          e.preventDefault();
          onIncrement();
        }
        break;
      // @ts-expect-error - Fallthrough is intentional
      case 'PageDown':
        if (onDecrementPage) {
          e.preventDefault();
          onDecrementPage();
          break;
        }
      // fallthrough
      case 'ArrowDown':
      case 'Down':
        if (onDecrement) {
          e.preventDefault();
          onDecrement();
        }
        break;
      case 'Home':
        if (onDecrementToMin) {
          e.preventDefault();
          onDecrementToMin();
        }
        break;
      case 'End':
        if (onIncrementToMax) {
          e.preventDefault();
          onIncrementToMax();
        }
        break;
      default:
        break;
    }
  };

  const isFocused = useRef(false);
  const onFocus = () => {
    isFocused.current = true;
  };

  const onBlur = () => {
    isFocused.current = false;
  };

  // Replace Unicode hyphen-minus (U+002D) with minus sign (U+2212).
  // This ensures that macOS VoiceOver announces it as "minus" even with other characters between the minus sign
  // and the number (e.g. currency symbol). Otherwise it announces nothing because it assumes the character is a hyphen.
  // In addition, replace the empty string with the word "Empty" so that iOS VoiceOver does not read "50%" for an empty field.
  const formattedTextValue =
    textValue === ''
      ? stringFormatter.format('Empty')
      : (textValue || `${value}`).replace('-', '\u2212');

  useEffect(() => {
    if (isFocused.current) {
      clearAnnouncer('assertive');
      announce(formattedTextValue, 'assertive');
    }
  }, [formattedTextValue]);

  const onIncrementPressStart = useEffectEvent((initialStepDelay: number) => {
    clearAsync();
    onIncrement?.();
    // Start spinning after initial delay
    _async.current = globalThis.setTimeout(() => {
      if (
        !isValidNumber(maxValue) ||
        !isValidNumber(value) ||
        value < maxValue
      ) {
        onIncrementPressStart(60);
      }
    }, initialStepDelay);
  });

  const onDecrementPressStart = useEffectEvent((initialStepDelay: number) => {
    clearAsync();
    onDecrement?.();
    // Start spinning after initial delay
    _async.current = globalThis.setTimeout(() => {
      if (minValue == null || value == null || value > minValue) {
        onDecrementPressStart(60);
      }
    }, initialStepDelay);
  });

  const cancelContextMenu: EventListener = (ev) => {
    ev.preventDefault();
  };

  const {addGlobalListener, removeAllGlobalListeners} = useGlobalListeners();

  return {
    spinButtonProps: {
      role: 'spinbutton',
      'aria-valuenow': isValidNumber(value) ? value : undefined,
      'aria-valuetext': textValue,
      'aria-valuemin': minValue,
      'aria-valuemax': maxValue,
      'aria-disabled': isDisabled,
      'aria-readonly': isReadOnly,
      'aria-required': isRequired,
      onKeyDown,
      onFocus,
      onBlur,
    },
    incrementButtonProps: {
      onPressStart: () => {
        onIncrementPressStart(400);
        addGlobalListener(globalThis, 'contextmenu', cancelContextMenu);
      },
      onPressEnd: () => {
        clearAsync();
        removeAllGlobalListeners();
      },
      onFocus,
      onBlur,
    },
    decrementButtonProps: {
      onPressStart: () => {
        onDecrementPressStart(400);
        addGlobalListener(globalThis, 'contextmenu', cancelContextMenu);
      },
      onPressEnd: () => {
        clearAsync();
        removeAllGlobalListeners();
      },
      onFocus,
      onBlur,
    },
  };
}
